import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { ChevronLeft, ChevronRight, XLg, GeoAltFill } from "react-bootstrap-icons";
import { useTour } from "./TourContext";
import "./Tour.css";

function getElementRect(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  return {
    top: rect.top - 6,
    left: rect.left - 6,
    width: rect.width + 12,
    height: rect.height + 12,
    element: el,
  };
}

function getTooltipPosition(spotRect, position, tooltipRef) {
  if (!spotRect || !tooltipRef.current) {
    return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  }

  const tooltip = tooltipRef.current.getBoundingClientRect();
  const gap = 16;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let top, left;

  switch (position) {
    case "bottom":
      top = spotRect.top + spotRect.height + gap;
      left = spotRect.left + spotRect.width / 2 - tooltip.width / 2;
      break;
    case "top":
      top = spotRect.top - tooltip.height - gap;
      left = spotRect.left + spotRect.width / 2 - tooltip.width / 2;
      break;
    case "left":
      top = spotRect.top + spotRect.height / 2 - tooltip.height / 2;
      left = spotRect.left - tooltip.width - gap;
      break;
    case "right":
      top = spotRect.top + spotRect.height / 2 - tooltip.height / 2;
      left = spotRect.left + spotRect.width + gap;
      break;
    default:
      top = spotRect.top + spotRect.height + gap;
      left = spotRect.left + spotRect.width / 2 - tooltip.width / 2;
  }

  if (left < 12) left = 12;
  if (left + tooltip.width > vw - 12) left = vw - tooltip.width - 12;
  if (top < 12) top = 12;
  if (top + tooltip.height > vh - 12) top = vh - tooltip.height - 12;

  return { top: `${top}px`, left: `${left}px` };
}

export default function TourOverlay() {
  const { tour, nextStep, prevStep, goToStep, endTour } = useTour();
  const [spotRect, setSpotRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({});
  const [isAnimating, setIsAnimating] = useState(false);
  const tooltipRef = useRef(null);
  const prevStepRef = useRef(-1);

  const step = tour ? tour.steps[tour.currentStep] : null;

  const updatePositions = useCallback(() => {
    if (!step) return;
    const rect = getElementRect(step.selector);
    setSpotRect(rect);

    if (rect && rect.element) {
      rect.element.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    requestAnimationFrame(() => {
      const pos = getTooltipPosition(rect, step.position || "bottom", tooltipRef);
      setTooltipPos(pos);
    });
  }, [step]);

  useEffect(() => {
    if (!tour) {
      setSpotRect(null);
      prevStepRef.current = -1;
      return;
    }

    setIsAnimating(true);
    const animTimer = setTimeout(() => setIsAnimating(false), 400);

    const posTimer = setTimeout(updatePositions, 100);

    prevStepRef.current = tour.currentStep;

    return () => {
      clearTimeout(animTimer);
      clearTimeout(posTimer);
    };
  }, [tour, updatePositions]);

  useEffect(() => {
    if (!tour) return;
    window.addEventListener("resize", updatePositions);
    window.addEventListener("scroll", updatePositions, true);
    return () => {
      window.removeEventListener("resize", updatePositions);
      window.removeEventListener("scroll", updatePositions, true);
    };
  }, [tour, updatePositions]);

  useEffect(() => {
    if (!tour) return;
    const handleKey = (e) => {
      if (e.key === "Escape") endTour();
      if (e.key === "ArrowRight") nextStep();
      if (e.key === "ArrowLeft") prevStep();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [tour, endTour, nextStep, prevStep]);

  if (!tour || !step) return null;

  const isFirst = tour.currentStep === 0;
  const isLast = tour.currentStep === tour.steps.length - 1;

  const overlay = (
    <div className="tour-overlay">
      <div className="tour-backdrop" onClick={endTour} />

      {spotRect && (
        <div
          className={`tour-spotlight ${isAnimating ? "animating" : ""}`}
          style={{
            top: `${spotRect.top}px`,
            left: `${spotRect.left}px`,
            width: `${spotRect.width}px`,
            height: `${spotRect.height}px`,
          }}
        />
      )}

      <div
        ref={tooltipRef}
        className={`tour-tooltip ${isAnimating ? "animating" : ""}`}
        style={tooltipPos}
      >
        <div className="tour-tooltip-header">
          <div className="tour-tooltip-step-info">
            <GeoAltFill size={12} className="tour-tooltip-icon" />
            <span className="tour-tooltip-screen-title">{tour.screenTitle}</span>
          </div>
          <button className="tour-tooltip-close" onClick={endTour}>
            <XLg size={12} />
          </button>
        </div>

        <h4 className="tour-tooltip-title">{step.title}</h4>
        <p className="tour-tooltip-content">{step.content}</p>

        <div className="tour-tooltip-progress">
          {tour.steps.map((_, i) => (
            <button
              key={i}
              className={`tour-progress-dot ${i === tour.currentStep ? "active" : ""} ${i < tour.currentStep ? "done" : ""}`}
              onClick={() => goToStep(i)}
            />
          ))}
        </div>

        <div className="tour-tooltip-footer">
          <button
            className="tour-btn tour-btn-prev"
            onClick={prevStep}
            disabled={isFirst}
          >
            <ChevronLeft size={12} />
            Anterior
          </button>
          <span className="tour-step-counter">
            {tour.currentStep + 1} de {tour.steps.length}
          </span>
          {isLast ? (
            <button className="tour-btn tour-btn-finish" onClick={endTour}>
              Concluir
            </button>
          ) : (
            <button className="tour-btn tour-btn-next" onClick={nextStep}>
              Proximo
              <ChevronRight size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(overlay, document.body);
}
